import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import useDC from '@eneco/dc/useDC';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { basketMachine } from '@eneco/flows/machines/basketMachine';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRedirect } from '@eneco/hooks/redirect';
import { InputText, RadioGroup, RadioTile } from '@eneco/sparky';
import { usePlaceholderContent, useLayoutData } from '@sitecore/common';
import { DecarbonizationLeadFlowNumberOfFloorsStepRendering } from '@sitecore/types/DecarbonizationLeadFlowNumberOfFloorsStep';
import { useMachine } from '@xstate/react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { DecarbonisationLeadFlowContext } from '../types';

interface FormFields {
  numberOfFloors: string;
  customNumberOfFloors: string;
}

const numberOfFloorsFieldName = 'numberOfFloors';
const customNumberOfFloorsFieldName = 'customNumberOfFloors';
const userInputOption = 'more';
const numberOfFloorsLimit = 20;
const getDefaultValue = (value: number | undefined) => {
  if (value) {
    return value > 5 ? userInputOption : `${value}`;
  }
  return undefined;
};

const getDefaultCustomNumberOfFloors = (value: number | undefined) =>
  getDefaultValue(value) === userInputOption ? `${value}` : undefined;

export const BuildingFloorsStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<DecarbonisationLeadFlowContext>();
  const sendToFlowMachine = useFlowActorRef().send;
  const [, sendToBasketMachine] = useMachine(basketMachine);
  const { businessUnit, label } = useDC();
  const redirect = useRedirect();
  const layoutData = useLayoutData();
  const flowContext = useFlowSelector(state => state.context);

  const data = usePlaceholderContent<{
    DecarbonizationLeadFlowNumberOfFloorsStep: DecarbonizationLeadFlowNumberOfFloorsStepRendering;
  }>();
  const { DecarbonizationLeadFlowNumberOfFloorsStep: { fields: textualData } = {} } = data;

  const schema = yup.object().shape({
    [numberOfFloorsFieldName]: yup
      .string()
      .required(textualData?.choice?.numberOfFloorsRadioGroupField?.value?.requiredMessage),
    [customNumberOfFloorsFieldName]: yup.string().when(numberOfFloorsFieldName, {
      is: (val: string) => val === userInputOption,
      then: schema => schema.required(textualData?.customersChoice?.numberOfFloorsInputField?.value?.requiredMessage),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const {
    control,
    handleSubmit,
    getValues,
    register,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    defaultValues: {
      numberOfFloors: getDefaultValue(flowContext.numberOfFloors),
      customNumberOfFloors: getDefaultCustomNumberOfFloors(flowContext.numberOfFloors),
    },
    mode: 'onSubmit',
    resolver: typedYupResolver<FormFields>(schema),
  });

  const numberOfFloors = watch(numberOfFloorsFieldName);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formValues = getValues();
      const numberOfFloors = +(formValues[numberOfFloorsFieldName] === userInputOption
        ? formValues[customNumberOfFloorsFieldName]
        : formValues[numberOfFloorsFieldName]);
      if (numberOfFloors > numberOfFloorsLimit) {
        sendToBasketMachine({
          type: 'UPDATE_BASKET',
          config: {
            businessUnit,
            label,
          },
          id: flowContext.basketId,
          values: {
            ...flowContext,
            // @ts-ignore basket machine does not respect the extended flow context type
            numberOfFloors,
            path: '/gegevens/',
          },
        });
        redirect({
          link: `${layoutData.route.fields?.frontEndRootPath?.value}meer-informatie`,
        });
      } else {
        sendToFlowMachine({
          type: 'NEXT_PAGE',
          values: {
            ...flowContext,
            numberOfFloors,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <RichText html={textualData?.content?.text?.value} />}
      </Header>
      <Controller
        control={control}
        name={numberOfFloorsFieldName}
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            aria-labelledby={name}
            name={name}
            direction="column"
            value={value}
            onValueChange={onChange}
            error={errors?.[name]?.message}>
            {textualData?.choice?.numberOfFloorsRadioGroupField?.value?.options?.map(option => (
              <RadioTile key={option.name} value={option.name}>
                {option.label}
              </RadioTile>
            ))}
          </RadioGroup>
        )}
      />
      {numberOfFloors === userInputOption && (
        <InputText
          {...register(customNumberOfFloorsFieldName)}
          label={textualData?.customersChoice?.numberOfFloorsInputField?.value?.label ?? ''}
          placeholder={textualData?.customersChoice?.numberOfFloorsInputField?.value?.placeholder}
          hint={textualData?.customersChoice?.numberOfFloorsInputField?.value?.hint}
          error={errors[customNumberOfFloorsFieldName]?.message}
        />
      )}

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
